import { configureStore } from '@reduxjs/toolkit';
import { getAsyncData, storeAsyncData } from '~global/helpers';
import app from './app/slice';
import auth from './user/reducers/auth';
import banners from './banners/slice';
import catalog from './catalog/reducers/catalog';
import cart from './cart/reducers/cart';
import voucher from './cart/reducers/voucher';
import address from './address/slice';
import location from './location/slice';
import shipping from './shipping/slice';
import profiling from './profiling/slice';
import promotion from './promotion/slice';
import search from './search/slice';
import help from './help/slice';
import tracker from './tracker/slice';
import payment from './payment/paymentSlice';
import modal from './modal/reducers/modal';
import orders from './orders/ordersSlice';
import creditDebitCard from './credit-debit-card/creditDebitCardSlice';
import productFilters from './product-filters/productFiltersSlice';
import categories from './categories/slice';
import brand from './brand/brandSlice';
import newArrival from './new-arrival/newArrivalSlice';
import bestSeller from './best-seller/bestSellerSlice';
import productDetail from './product-detail/slice';
import productDetailEgift from './product-detail-egift/slice';
import campaign from './campaign/slice';
import campaignV2 from './campaignV2/slice';

import myReviews from './my-reviews/slice';
import createReview from './create-review/slice';
import editReview from './edit-review/slice';
import addPhoto from './add-photo/slice';
import careers from './careers/slice';
import flashSale from './flash-sale/slice';
import pos from './pos/slice';
import home from './home/slice';
import shoppingBag from './shopping-bag/slice';
import egiftList from './e-gift-list/egiftListSlice';
import babyRegistry from './baby-registry/slice';
import gifting from './gifting/slice';
import giftingCategoryDetail from './giftingCategoryDetail/slice';
import membership from './membership/slice';
import notificationCenter, {
  actions as notificationCenterActions,
} from './notification-center/slice';
import momChecklist from './mom-checklist/slice';
import article from './articles/slice';
// Revamp V2
import newProductFilters from './product-filters/v2/productFiltersSlice';
import newLillaCategory from './new-lilla-category/slice';
import eWallet from './e-wallet/eWalletSlice';
import insiderTracking from './insiderTracking/slice';
import bundlePacks from './bundle-packs/slice';
import refund from './refund/slice';
import affiliate from './affiliate/slice';

const store = configureStore({
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Modal data intentionally carries callbacks/refs (e.g.
      // navigateToProceedRefund, showDetailRefundRef) between the screen
      // that opens a modal and the modal itself, so `modal` state and
      // `MODAL/SHOW` actions are not expected to be serializable.
      serializableCheck: {
        ignoredActions: ['MODAL/SHOW'],
        ignoredPaths: ['modal'],
      },
    }),
  reducer: {
    app,
    auth,
    banners,
    catalog,
    cart,
    payment,
    modal,
    voucher,
    address,
    location,
    shipping,
    orders,
    creditDebitCard,
    profiling,
    promotion,
    search,
    help,
    tracker,
    productFilters,
    categories,
    brand,
    newArrival,
    bestSeller,
    productDetail,
    productDetailEgift,
    campaign,
    campaignV2,
    myReviews,
    createReview,
    editReview,
    addPhoto,
    careers,
    flashSale,
    pos,
    home,
    shoppingBag,
    egiftList,
    babyRegistry,
    membership,
    notificationCenter,

    // Revamp V2
    newProductFilters,
    gifting,
    giftingCategoryDetail,
    momChecklist,
    article,
    newLillaCategory,
    eWallet,
    insiderTracking,
    bundlePacks,
    refund,
    affiliate,
  },
});

// Notification tab visibility (order/loyalty toggles) is otherwise purely
// in-memory redux state, so it would reset to defaults on every app
// restart. Persist it to AsyncStorage and hydrate it back on boot so a
// disabled tab (and the point in time it was disabled) survives a refresh.
const NOTIFICATION_VISIBILITY_META_KEY = '@notification_center_visibility_meta';
const getNotificationVisibilityMeta = (state) => ({
  visibilityByTab: state.visibilityByTab,
  visibilityDisabledAtByTab: state.visibilityDisabledAtByTab,
  frozenUnreadCountByTab: state.frozenUnreadCountByTab,
});

let lastPersistedNotificationVisibilityMeta = null;

getAsyncData(NOTIFICATION_VISIBILITY_META_KEY).then((meta) => {
  if (!meta) {
    return;
  }

  store.dispatch(notificationCenterActions.hydrateVisibilityMeta(meta));
  lastPersistedNotificationVisibilityMeta = JSON.stringify(
    getNotificationVisibilityMeta(store.getState().notificationCenter),
  );
});

store.subscribe(() => {
  const meta = getNotificationVisibilityMeta(
    store.getState().notificationCenter,
  );
  const serializedMeta = JSON.stringify(meta);

  if (serializedMeta !== lastPersistedNotificationVisibilityMeta) {
    lastPersistedNotificationVisibilityMeta = serializedMeta;
    storeAsyncData(NOTIFICATION_VISIBILITY_META_KEY, meta);
  }
});

export default store;
